import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Mail } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { AUTH } from "@/constants/testIds";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success("If an account exists, a reset link has been sent.");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background grain px-4 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-secondary" /></div>
          <span className="font-display font-semibold text-lg">Treasurescare</span>
        </Link>
        <div className="overline text-secondary">Password reset</div>
        <h1 className="mt-2 font-display text-4xl font-light tracking-tight">Forgot your password?</h1>
        <p className="mt-2 text-muted-foreground text-sm">Pop in your email and we'll send you a reset link.</p>

        {sent ? (
          <div className="mt-8 p-5 bg-primary/5 border border-primary/20 rounded-xl">
            <div className="font-medium">Check your inbox.</div>
            <div className="text-sm text-muted-foreground mt-1">
              If <span className="font-mono">{email}</span> is registered, a reset link will arrive in a moment. The link expires in 1 hour.
            </div>
            <div className="mt-4 text-xs text-muted-foreground">
              (In this preview, the reset link is also logged to the notifications inbox and backend logs.)
            </div>
            <Link to="/login"><Button variant="outline" className="mt-5" data-testid="back-to-login-btn">Back to sign in</Button></Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <Label className="text-xs">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                <Input required type="email" data-testid="forgot-email-input" value={email} onChange={e => setEmail(e.target.value)} className="pl-9" placeholder="you@example.com" />
              </div>
            </div>
            {err && <div className="text-sm text-destructive">{err}</div>}
            <Button type="submit" data-testid="forgot-submit-btn" disabled={loading} className="w-full">
              {loading ? "Sending…" : "Send reset link"}
            </Button>
            <div className="text-sm text-center text-muted-foreground">
              Remembered it? <Link to="/login" className="text-primary hover:underline">Sign in</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
